/**
 * Storage + access for the Supabase-compatible JWT minted at login (Option A of
 * the tenant-isolation rollout). Kept in its own dependency-free module so both
 * `supabase.ts` (which reads the token) and `auth.ts` (which stores it) can use
 * it without creating an import cycle.
 *
 * The token is only *used* by the supabase-js client when the
 * `VITE_SUPABASE_RLS_TOKEN` flag is on; until then it is merely stored, so
 * enabling per-tenant RLS is a controlled, reversible switch.
 */

export const SB_ACCESS_TOKEN_KEY = 'sb_access_token'

/** Read a JWT `exp` (ms since epoch) without verifying the signature. */
function jwtExpMs(token: string): number | null {
	try {
		const part = token.split('.')[1]
		if (!part) return null
		const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'))
		const payload = JSON.parse(json) as { exp?: number }
		return typeof payload.exp === 'number' ? payload.exp * 1000 : null
	} catch {
		return null
	}
}

/** The stored Supabase token, or null when absent or expired. */
export function getStoredSupabaseToken(): string | null {
	try {
		const token = localStorage.getItem(SB_ACCESS_TOKEN_KEY)
		if (!token) return null
		const expMs = jwtExpMs(token)
		if (expMs !== null && Date.now() >= expMs) return null
		return token
	} catch {
		return null
	}
}

export function setStoredSupabaseToken(token: string | null | undefined): void {
	try {
		if (token) localStorage.setItem(SB_ACCESS_TOKEN_KEY, token)
		else localStorage.removeItem(SB_ACCESS_TOKEN_KEY)
	} catch {
		/* localStorage unavailable — ignore */
	}
}

export function clearStoredSupabaseToken(): void {
	setStoredSupabaseToken(null)
}
